/**
 * Multi-select dropdown (§A queue filters) — a sibling of <Select> that shares
 * the `.xselect` look + keyboard model but toggles a set of values and stays
 * open while you pick. The trigger summarises the selection ("3 carriers"); a
 * footer offers Clear. Fully keyboard-operable:
 *   - closed trigger: Enter/Space/↓ opens
 *   - open: ↑/↓ move the active option, Enter/Space TOGGLES it (menu stays open),
 *     Esc/Tab close, click-out closes
 *   - non-searchable: type-ahead; searchable: a filter input narrows the list.
 * ARIA: role=listbox aria-multiselectable; each option aria-selected reflects
 * membership; active option tracked via aria-activedescendant.
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { moveSelection } from "../../lib/keyboardNav.js";
import { Icon } from "./Icon.js";
import type { SelectOption } from "./Select.js";

export function MultiSelect({
  value,
  onChange,
  options,
  placeholder = "Any",
  ariaLabel,
  searchable = false,
  searchPlaceholder = "Type to search…",
  /** Plural noun for the "N <noun>" summary when >1 is selected. */
  summaryNoun = "selected",
  className,
}: {
  value: string[];
  onChange: (value: string[]) => void;
  options: SelectOption[];
  placeholder?: string;
  ariaLabel?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  summaryNoun?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const optionRefs = useRef<Map<number, HTMLLIElement>>(new Map());
  const typeahead = useRef<{ buf: string; at: number }>({ buf: "", at: 0 });
  const baseId = useId();

  const selectedSet = new Set(value);
  const shown =
    searchable && query.trim()
      ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
      : options;

  // Trigger summary: placeholder when empty, the single label when 1, "N noun" otherwise.
  const summary =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? (options.find((o) => o.value === value[0])?.label ?? value[0]!)
        : `${value.length} ${summaryNoun}`;

  const close = useCallback((focusTrigger = true) => {
    setOpen(false);
    setQuery("");
    setActive(-1);
    if (focusTrigger) triggerRef.current?.focus();
  }, []);

  const toggle = useCallback(
    (v: string) => {
      onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
    },
    [onChange, value],
  );

  const openMenu = useCallback(() => {
    setOpen(true);
    // Start on the first selected option if any, else the top.
    const idx = value.length ? options.findIndex((o) => selectedSet.has(o.value)) : 0;
    setActive(idx >= 0 ? idx : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, value]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    if (searchable) searchRef.current?.focus();
    else listRef.current?.focus();
  }, [open, searchable]);
  useEffect(() => {
    if (open && active >= 0) optionRefs.current.get(active)?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  const onListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => moveSelection(i, 1, shown.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => moveSelection(i, -1, shown.length));
    } else if (e.key === "Home") {
      e.preventDefault();
      setActive(shown.length ? 0 : -1);
    } else if (e.key === "End") {
      e.preventDefault();
      setActive(shown.length - 1);
    } else if (e.key === "Enter" || (e.key === " " && !searchable)) {
      e.preventDefault();
      const opt = active >= 0 ? shown[active] : undefined;
      if (opt) toggle(opt.value); // toggle, do NOT close — multi-pick
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "Tab") {
      e.preventDefault();
      close();
    } else if (!searchable && e.key.length === 1 && /\S/.test(e.key)) {
      const now = Date.now();
      const ta = typeahead.current;
      ta.buf = now - ta.at > 600 ? e.key : ta.buf + e.key;
      ta.at = now;
      const q = ta.buf.toLowerCase();
      const idx = shown.findIndex((o) => o.label.toLowerCase().startsWith(q));
      if (idx >= 0) setActive(idx);
    }
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      openMenu();
    }
  };

  const listboxId = `${baseId}-listbox`;
  const activeId = active >= 0 && active < shown.length ? `${baseId}-opt-${active}` : undefined;

  return (
    <div ref={rootRef} className={`xselect xselect--multi${className ? ` ${className}` : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className="xselect__trigger control"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel ? `${ariaLabel}${value.length ? ` (${value.length} selected)` : ""}` : undefined}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={value.length ? "xselect__value" : "xselect__value xselect__value--placeholder"}>
          {summary}
        </span>
        {value.length > 1 && <span className="xselect__count" aria-hidden="true">{value.length}</span>}
        <span className="xselect__chevron" aria-hidden="true" />
      </button>

      {open && (
        <div className="xselect__menu" role="presentation" onKeyDown={onListKeyDown}>
          {searchable && (
            <div className="xselect__search">
              <Icon name="search" size={14} />
              <input
                ref={searchRef}
                className="xselect__search-input"
                placeholder={searchPlaceholder}
                aria-label={ariaLabel ?? "Search"}
                value={query}
                role="combobox"
                aria-expanded
                aria-haspopup="listbox"
                aria-autocomplete="list"
                aria-controls={listboxId}
                aria-activedescendant={activeId}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
              />
            </div>
          )}
          <ul
            ref={listRef}
            id={listboxId}
            className="xselect__list"
            role="listbox"
            aria-multiselectable="true"
            aria-label={ariaLabel}
            tabIndex={searchable ? -1 : 0}
            aria-activedescendant={searchable ? undefined : activeId}
            onKeyDown={searchable ? undefined : onListKeyDown}
          >
            {shown.length === 0 ? (
              <li className="xselect__empty" role="presentation">
                No matches
              </li>
            ) : (
              shown.map((o, i) => {
                const isSel = selectedSet.has(o.value);
                return (
                  <li
                    key={o.value}
                    id={`${baseId}-opt-${i}`}
                    ref={(el) => {
                      if (el) optionRefs.current.set(i, el);
                      else optionRefs.current.delete(i);
                    }}
                    role="option"
                    aria-selected={isSel}
                    className={"xselect__option" + (i === active ? " xselect__option--active" : "") + (isSel ? " xselect__option--selected" : "")}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => toggle(o.value)}
                  >
                    <span className="xselect__check xselect__check--box" aria-hidden="true">
                      {isSel && <Icon name="check" size={13} />}
                    </span>
                    {o.label}
                  </li>
                );
              })
            )}
          </ul>
          {value.length > 0 && (
            <div className="xselect__footer">
              <span className="xselect__footer-count">{value.length} selected</span>
              <button type="button" className="xselect__clear" onClick={() => onChange([])}>
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
